import React from "react";
import { Check, MapPin, ChevronDown, Building2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { useLocations } from "@/lib/locations";
import { useAuth } from "@/lib/auth";
import { useNavigate } from "react-router-dom";

export function LocationSwitcher() {
  const { locations, activeId, activeLocation, setLocation } = useLocations() || {};
  const { company } = useAuth();
  const navigate = useNavigate();
  const plan = company?.plan || "free_trial";
  const canMulti = plan === "enterprise" || plan === "free_trial";

  if (!locations || locations.length === 0) return null;

  // If only one location and not enterprise, show plain badge
  if (locations.length <= 1 && !canMulti) {
    return (
      <div className="hidden sm:flex items-center gap-1.5 text-xs text-muted-foreground rounded-full border border-border bg-card px-2.5 py-1" data-testid="location-switcher-single">
        <MapPin className="w-3.5 h-3.5" />
        <span>{locations[0]?.name || "Main"}</span>
      </div>
    );
  }

  const label = activeId === "all" ? "All locations" : (activeLocation?.name || "Location");

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="h-8 gap-1.5 rounded-full border border-border px-2.5"
          data-testid="location-switcher-trigger"
        >
          <MapPin className="w-3.5 h-3.5 text-primary" />
          <span className="text-xs font-medium truncate max-w-[140px]">{label}</span>
          <ChevronDown className="w-3.5 h-3.5 text-muted-foreground" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[220px]">
        <DropdownMenuLabel className="text-[11px] uppercase tracking-wider text-muted-foreground">
          Workspace location
        </DropdownMenuLabel>
        <DropdownMenuItem onClick={() => setLocation("all")} data-testid="location-switcher-all">
          <Building2 className="w-4 h-4 mr-2" />
          <span className="flex-1">All locations</span>
          {activeId === "all" && <Check className="w-4 h-4 text-primary" />}
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        {locations.map((l) => (
          <DropdownMenuItem key={l.id} onClick={() => setLocation(l.id)} data-testid={`location-switcher-item-${l.id}`}>
            <MapPin className="w-4 h-4 mr-2 text-muted-foreground" />
            <div className="flex-1 min-w-0">
              <div className="text-sm truncate">{l.name}</div>
              {l.address && <div className="text-[11px] text-muted-foreground truncate">{l.address}</div>}
            </div>
            {activeId === l.id && <Check className="w-4 h-4 text-primary" />}
          </DropdownMenuItem>
        ))}
        {canMulti && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => navigate("/app/locations")} data-testid="location-switcher-manage">
              <Building2 className="w-4 h-4 mr-2" />
              <span>Manage locations…</span>
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
